import { useState, useEffect, useCallback, useRef } from "react";
import { doc, getDoc, updateDoc, collection, query, where, getDocs, limit } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { db, auth } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { useSalePage } from "../../contexts/SalePageContext";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import MediaUpload from "../../components/MediaUpload";
import {
  Settings, User, Globe, Save, CheckCircle, Loader, X, Check, Copy, AlertCircle,
  Info, Clock, CreditCard, LayoutDashboard, Sparkles, ChevronDown, Crown,
  MessageSquare, Receipt, RefreshCw, LogOut, Lightbulb,
} from "lucide-react";

// ---- design tokens (from Claude Design: Settings.dc.html) ----
const FONT = '"IBM Plex Sans Thai", system-ui, sans-serif';
const HEAD = "'Anuphan', sans-serif";
const INK = "#0E3A40";

function planLabel(plan?: string) {
  if (!plan) return "สมาชิก";
  if (plan === "free") return "ทดสอบระบบ";
  return plan.toUpperCase();
}

export default function SalePageSettings() {
  const { user } = useAuthContext();
  const { subscription, isSubscriptionExpired } = useSalePage();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [profileImageKey, setProfileImageKey] = useState(Date.now());
  const [username, setUsername] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"profile" | "url">("profile");
  const [copied, setCopied] = useState(false);

  // Username validation states
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameErrorMessage, setUsernameErrorMessage] = useState<string | null>(null);
  const [originalUsername, setOriginalUsername] = useState("");

  // URL addon states
  const [hasUrlAddon, setHasUrlAddon] = useState(false);
  const [urlAddonType, setUrlAddonType] = useState<"path" | "subdomain" | "both" | null>(null);

  // header dropdowns
  const [featOpen, setFeatOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const featRef = useRef<HTMLDivElement>(null);
  const acctRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (featRef.current && !featRef.current.contains(t)) setFeatOpen(false);
      if (acctRef.current && !acctRef.current.contains(t)) setAcctOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const handleProfileUploadComplete = useCallback((mediaId: string, url: string) => {
    console.log("[SalePageSettings] Profile upload complete:", { mediaId, url });
    setProfileImage(url);
    setProfileImageKey(Date.now());
  }, []);

  useEffect(() => {
    async function loadProfile() {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setName(data.name || "");
          setBusinessName(data.businessName || "");
          setBusinessDescription(data.businessDescription || "");
          setPhone(data.phone || "");
          setProfileImage(data.profileImage || "");
          const loadedUsername = data.salePageUsername || "";
          setUsername(loadedUsername);
          setOriginalUsername(loadedUsername);
        }
      } catch (err) {
        console.error("Error loading profile:", err);
      } finally {
        setLoading(false);
      }
    }
    loadProfile();
  }, [user]);

  // Check for URL addon
  useEffect(() => {
    const checkUrlAddon = async () => {
      if (!user?.uid) return;
      try {
        const urlAddonDoc = await getDoc(doc(db, "salePageUrlAddons", user.uid));
        if (urlAddonDoc.exists()) {
          const data = urlAddonDoc.data();
          setHasUrlAddon(data.status === "active");
          setUrlAddonType(data.type);
        }
      } catch (error) {
        console.error("Error checking URL addon:", error);
      }
    };
    checkUrlAddon();
  }, [user]);

  // Username validation function
  const validateUsername = async (usernameToCheck: string) => {
    if (!usernameToCheck || usernameToCheck.trim() === "") {
      setUsernameAvailable(null);
      setUsernameErrorMessage(null);
      return;
    }
    if (usernameToCheck === originalUsername) {
      setUsernameAvailable(true);
      setUsernameErrorMessage(null);
      return;
    }
    setIsCheckingUsername(true);
    setUsernameAvailable(null);
    setUsernameErrorMessage(null);

    const normalizedUsername = usernameToCheck.toLowerCase().trim();

    if (normalizedUsername.length < 3 || normalizedUsername.length > 30) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage("ความยาว 3-30 ตัวอักษร");
      return;
    }
    const hasThai = /[฀-๿]/.test(normalizedUsername);
    if (hasThai) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage("ไม่สามารถใช้ภาษาไทยได้ กรุณาใช้ภาษาอังกฤษเท่านั้น");
      return;
    }
    const validFormat = /^[a-z0-9_-]+$/.test(normalizedUsername);
    if (!validFormat) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage("ใช้ได้เฉพาะ a-z, 0-9, _, - เท่านั้น");
      return;
    }
    const reserved = [
      "www", "api", "admin", "cdn", "mail", "ftp", "smtp", "salepage", "edumi",
      "official", "office", "support", "help", "service", "customer",
      "owner", "founder", "ceo", "director", "manager",
      "info", "contact", "about", "company", "corporate",
      "dev", "test", "demo", "beta", "staging", "production",
      "system", "root", "administrator", "account", "billing", "payment",
    ];
    if (reserved.includes(normalizedUsername)) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage("ชื่อนี้ถูกสงวนไว้สำหรับระบบ");
      return;
    }
    try {
      const usersQuery = query(
        collection(db, "users"),
        where("salePageUsername", "==", normalizedUsername),
        limit(1)
      );
      const usersSnapshot = await getDocs(usersQuery);
      if (!usersSnapshot.empty) {
        setIsCheckingUsername(false);
        setUsernameAvailable(false);
        setUsernameErrorMessage("ชื่อนี้ถูกใช้งานแล้ว");
        return;
      }
      const salePageQuery = query(
        collection(db, "salePageSubscriptions"),
        where("username", "==", normalizedUsername),
        where("hasSubdomain", "==", true),
        limit(1)
      );
      const salePageSnapshot = await getDocs(salePageQuery);
      if (!salePageSnapshot.empty) {
        setIsCheckingUsername(false);
        setUsernameAvailable(false);
        setUsernameErrorMessage("ชื่อนี้ถูกใช้งานแล้ว");
        return;
      }
      setIsCheckingUsername(false);
      setUsernameAvailable(true);
      setUsernameErrorMessage(null);
    } catch (error) {
      console.error("Error checking subdomain:", error);
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage("เกิดข้อผิดพลาดในการตรวจสอบ กรุณาลองใหม่");
    }
  };

  useEffect(() => {
    if (!username || !hasUrlAddon) return;
    const timeoutId = setTimeout(() => validateUsername(username), 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, hasUrlAddon, originalUsername]);

  const isExpired = isSubscriptionExpired();

  const handleSave = async () => {
    if (!user) return;
    if (isExpired) {
      alert("แพ็คเก็จหมดอายุแล้ว กรุณาต่ออายุเพื่อใช้งานฟีเจอร์นี้");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const updateData: any = {
        name: name.trim(),
        businessName: businessName.trim(),
        businessDescription: businessDescription.trim(),
        phone: phone.trim(),
        profileImage: profileImage.trim(),
      };
      if (hasUrlAddon && username.trim()) {
        updateData.salePageUsername = username.trim().toLowerCase();
      }
      await updateDoc(doc(db, "users", user.uid), updateData);
      setMessage("✅ บันทึกข้อมูลสำเร็จ!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err: any) {
      console.error("Error saving profile:", err);
      setError(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => {
    try {
      localStorage.removeItem("auth_user_cache");
      await signOut(auth);
    } catch { /* ignore */ }
    navigate("/");
  };

  const copyFreeUrl = () => {
    if (isExpired) {
      alert("แพ็คเก็จหมดอายุแล้ว กรุณาต่ออายุเพื่อใช้งานฟีเจอร์นี้");
      return;
    }
    try { navigator.clipboard.writeText(`https://saledi.web.app/s/${user?.uid}`); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const acctName: string =
    (user as any)?.displayName || (user as any)?.name || (user as any)?.username ||
    (user?.email || "").split("@")[0] || "บัญชีของฉัน";

  // ---- shared inline styles ----
  const inputStyle: React.CSSProperties = {
    border: "1px solid rgba(14,58,64,0.14)", borderRadius: 12, padding: "12px 14px",
    fontSize: 14.5, color: INK, background: isExpired ? "#F1F5F5" : "#fff",
    fontFamily: FONT, width: "100%",
  };
  const labelWrap: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 7 };
  const cardStyle: React.CSSProperties = {
    background: "#fff", borderRadius: 24, padding: "clamp(22px,2.8vw,30px)",
    border: "1px solid rgba(14,58,64,0.06)", boxShadow: "0 30px 60px -48px rgba(14,58,64,0.35)",
    display: "flex", flexDirection: "column", gap: 22,
  };
  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    border: 0, borderRadius: 15, padding: 13, fontFamily: HEAD, fontSize: 14.5, fontWeight: 500,
    cursor: "pointer",
    background: active ? "linear-gradient(120deg, #00B4A1, #3A93B9)" : "transparent",
    color: active ? "#fff" : "#5B7D80",
  });
  const menuLink: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 10, padding: "11px 12px",
    borderRadius: 11, fontSize: 14, color: INK, textDecoration: "none", cursor: "pointer",
  };
  const hasValidSub = !!subscription && (subscription.status === "active" || subscription.status === "trial");
  const featureItems: { to: string; label: string; icon: JSX.Element; show?: boolean }[] = [
    { to: "/salepage/messages", label: "ข้อความ", icon: <MessageSquare style={{ width: 16, height: 16, color: "#3A93B9" }} />, show: hasValidSub },
    { to: "/salepage/payments", label: "การชำระเงิน", icon: <CreditCard style={{ width: 16, height: 16, color: "#00B4A1" }} />, show: hasValidSub },
    { to: "/salepage/payment-history", label: "ประวัติการชำระ", icon: <Receipt style={{ width: 16, height: 16, color: "#5B6FCB" }} /> },
    { to: "/salepage/settings", label: "ตั้งค่า", icon: <Settings style={{ width: 16, height: 16, color: "#5B7D80" }} /> },
    { to: "/salepage/renew", label: "ต่ออายุ / แพ็คเกจ", icon: <RefreshCw style={{ width: 16, height: 16, color: "#FF7A45" }} /> },
  ].filter((f) => f.show !== false);

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#F3F8F8", color: INK, fontFamily: FONT }}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {/* Header */}
      <header style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(243,248,248,0.9)", backdropFilter: "blur(18px)", borderBottom: "1px solid rgba(14,58,64,0.08)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "12px clamp(16px,3vw,32px)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }}>
            <span style={{ width: 32, height: 32, borderRadius: 10, background: "linear-gradient(135deg, #00B4A1, #3A93B9)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: HEAD, fontWeight: 700, fontSize: 16, boxShadow: "0 8px 18px -8px rgba(0,180,161,0.55)" }}>S</span>
            <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 19, letterSpacing: "-0.01em" }}>SaleDi</span>
          </div>

          <nav style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 2, background: "oklch(0.96 0.016 175)", border: "1px solid oklch(0.915 0.022 175)", borderRadius: 999, padding: 4, position: "relative" }} ref={featRef}>
            <button type="button" onClick={() => navigate("/dashboard")} style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: FONT, fontSize: 14, color: "#3C6367", padding: "8px 15px", borderRadius: 999, border: 0, background: "transparent", cursor: "pointer" }}>
              <LayoutDashboard style={{ width: 15, height: 15 }} /> Dashboard
            </button>
            <button type="button" onClick={() => setFeatOpen((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: FONT, fontSize: 14, color: "#3C6367", padding: "8px 15px", borderRadius: 999, border: 0, background: "transparent", cursor: "pointer" }}>
              <Sparkles style={{ width: 15, height: 15, color: "#5B6FCB" }} /> ฟีเจอร์ของฉัน <ChevronDown style={{ width: 13, height: 13 }} />
            </button>
            {featOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 210, background: "#fff", border: "1px solid rgba(14,58,64,0.1)", borderRadius: 16, boxShadow: "0 26px 50px -22px rgba(14,58,64,0.4)", padding: 8, zIndex: 80 }}>
                {featureItems.map((f) => (
                  <div key={f.to} style={menuLink} onClick={() => { setFeatOpen(false); navigate(f.to); }}>{f.icon} {f.label}</div>
                ))}
              </div>
            )}
          </nav>

          <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative" }} ref={acctRef}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, background: "#0E3A40", color: "#fff", fontSize: 12.5, fontWeight: 500, padding: "6px 13px", borderRadius: 999 }}>
              <Crown style={{ width: 13, height: 13, color: "#FF7A45" }} /> {planLabel(subscription?.plan)}
            </span>
            <button type="button" onClick={() => setAcctOpen((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 8, border: 0, background: "transparent", cursor: "pointer", padding: 0 }}>
              <span style={{ width: 38, height: 38, borderRadius: 999, background: "#EAF1F1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <User style={{ width: 18, height: 18, color: "#3A93B9" }} />
              </span>
              <ChevronDown style={{ width: 15, height: 15, color: "#5B7D80" }} />
            </button>
            {acctOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 230, background: "#fff", border: "1px solid rgba(14,58,64,0.1)", borderRadius: 16, boxShadow: "0 26px 50px -22px rgba(14,58,64,0.4)", padding: 8, zIndex: 80 }}>
                <div style={{ padding: "10px 12px", borderBottom: "1px solid #EEF4F4", marginBottom: 6 }}>
                  <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 14, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{acctName}</div>
                  <div style={{ fontSize: 12, color: "#5B7D80", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email || ""}</div>
                </div>
                <div style={menuLink} onClick={() => { setAcctOpen(false); navigate("/salepage/settings"); }}><Settings style={{ width: 16, height: 16 }} /> ตั้งค่า</div>
                <div style={{ ...menuLink, color: "#C0392B" }} onClick={() => { setAcctOpen(false); doLogout(); }}><LogOut style={{ width: 16, height: 16 }} /> ออกจากระบบ</div>
              </div>
            )}
          </div>
        </div>
      </header>

      {loading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center" }}>
            <Loader style={{ width: 40, height: 40, color: "#00B4A1", margin: "0 auto 12px" }} className="animate-spin" />
            <p style={{ color: "#5B7D80" }}>กำลังโหลด...</p>
          </div>
        </div>
      ) : (
        <>
          <main style={{ flex: 1, maxWidth: 900, margin: "0 auto", width: "100%", padding: "clamp(22px,3vw,34px) clamp(16px,3vw,32px) clamp(100px,11vw,130px)", display: "flex", flexDirection: "column", gap: "clamp(18px,2.4vw,24px)" }}>
            {/* Title */}
            <div>
              <h1 style={{ fontFamily: HEAD, fontWeight: 700, fontSize: "clamp(24px,2.6vw,30px)", margin: "0 0 4px", letterSpacing: "-0.02em" }}>ตั้งค่า</h1>
              <p style={{ fontSize: 14, color: "#5B7D80", margin: 0 }}>จัดการข้อมูลส่วนตัวและ URL ของ Salepage คุณ</p>
            </div>

            {/* Expired banner */}
            {isExpired && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 16, padding: "14px 18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Clock style={{ width: 20, height: 20, color: "#EF4444", flex: "0 0 auto" }} />
                  <div style={{ fontSize: 13.5, color: "#B91C1C" }}>แพ็คเก็จหมดอายุแล้ว — ต่ออายุเพื่อแก้ไขข้อมูลและใช้งานฟีเจอร์ต่างๆ</div>
                </div>
                <button type="button" onClick={() => navigate("/salepage/renew")} style={{ display: "flex", alignItems: "center", gap: 6, background: "#EF4444", color: "#fff", border: 0, borderRadius: 999, padding: "9px 16px", fontFamily: HEAD, fontWeight: 600, fontSize: 13.5, cursor: "pointer", whiteSpace: "nowrap" }}>
                  <CreditCard style={{ width: 15, height: 15 }} /> ต่ออายุเลย
                </button>
              </div>
            )}

            {/* Tabs */}
            <div style={{ display: "flex", gap: 6, background: "#fff", borderRadius: 20, padding: 6, border: "1px solid rgba(14,58,64,0.06)", boxShadow: "0 20px 40px -34px rgba(14,58,64,0.3)" }}>
              <button type="button" onClick={() => setActiveTab("profile")} style={tabStyle(activeTab === "profile")}>
                <User style={{ width: 16, height: 16 }} /> ข้อมูลส่วนตัว
              </button>
              <button type="button" onClick={() => setActiveTab("url")} style={tabStyle(activeTab === "url")}>
                <Globe style={{ width: 16, height: 16 }} /> URL
              </button>
            </div>

            {/* Profile tab */}
            {activeTab === "profile" && (
              <div style={cardStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 44, height: 44, borderRadius: 13, background: "linear-gradient(135deg, #00B4A1, #3A93B9)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                    <User style={{ width: 20, height: 20 }} />
                  </span>
                  <div>
                    <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 19 }}>ข้อมูลส่วนตัวและธุรกิจ</div>
                    <div style={{ fontSize: 13.5, color: "#5B7D80" }}>ข้อมูลพื้นฐานและรูปภาพของคุณ</div>
                  </div>
                </div>

                {/* Profile image — horizontal */}
                <div style={{ background: "rgba(0,180,161,0.08)", borderRadius: 18, padding: "clamp(16px,2vw,20px)", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  {profileImage ? (
                    <>
                      <div style={{ width: 64, height: 64, borderRadius: 999, overflow: "hidden", flex: "0 0 auto", border: "3px solid #fff", boxShadow: "0 8px 18px -10px rgba(14,58,64,0.5)", background: "#EAF1F1" }}>
                        <img key={`profile-${profileImageKey}`} src={profileImage} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                      </div>
                      <div>
                        <button type="button" onClick={() => { setProfileImage(""); setProfileImageKey(Date.now()); }} style={{ fontSize: 13.5, fontWeight: 600, color: "#3A93B9", background: "transparent", border: 0, cursor: "pointer", padding: 0 }}>
                          อัปโหลดรูปใหม่
                        </button>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#5B7D80", marginTop: 3 }}>
                          <Lightbulb style={{ width: 12, height: 12, color: "#FF7A45" }} /> แนะนำ: รูปสี่เหลี่ยมจัตุรัส 500×500px (อัตราส่วน 1:1)
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ flex: "1 1 200px", minWidth: 200 }}>
                        <MediaUpload
                          key="profile-upload"
                          courseId={user?.uid || "temp"}
                          mediaType="image"
                          onUploadComplete={handleProfileUploadComplete}
                          onUploadError={(err) => alert(err)}
                          compact={true}
                          aspectRatio={1}
                        />
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#5B7D80" }}>
                        <Lightbulb style={{ width: 12, height: 12, color: "#FF7A45" }} /> แนะนำ: รูปสี่เหลี่ยมจัตุรัส 500×500px (1:1)
                      </div>
                    </>
                  )}
                </div>

                {/* Name + phone grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,220px),1fr))", gap: 16 }}>
                  <label style={labelWrap}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>ชื่อ-นามสกุล <span style={{ color: "#FF7A45" }}>*</span></span>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="เช่น: สมชาย ใจดี" disabled={isExpired} style={inputStyle} />
                  </label>
                  <label style={labelWrap}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>เบอร์โทรศัพท์</span>
                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="เช่น: 081-234-5678" disabled={isExpired} style={inputStyle} />
                  </label>
                </div>
                {/* Business name */}
                <label style={labelWrap}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>ชื่อธุรกิจ/แบรนด์</span>
                  <input type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="เช่น: ร้านขายของออนไลน์" disabled={isExpired} style={inputStyle} />
                </label>
                {/* Business description */}
                <label style={labelWrap}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>คำอธิบายธุรกิจ</span>
                  <textarea value={businessDescription} onChange={(e) => setBusinessDescription(e.target.value)} placeholder="แนะนำธุรกิจของคุณสั้นๆ..." rows={4} disabled={isExpired} style={{ ...inputStyle, resize: "vertical" }} />
                </label>
              </div>
            )}

            {/* URL tab */}
            {activeTab === "url" && (
              <div style={cardStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 44, height: 44, borderRadius: 13, background: "linear-gradient(135deg, #00B4A1, #3A93B9)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                    <Globe style={{ width: 20, height: 20 }} />
                  </span>
                  <div>
                    <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 19 }}>URL สำหรับ Salepage</div>
                    <div style={{ fontSize: 13.5, color: "#5B7D80" }}>จัดการ URL เพื่อให้ลูกค้าเข้าถึง Salepage ของคุณ</div>
                  </div>
                </div>

                {/* Free URL */}
                <div style={{ border: "1.5px solid rgba(0,180,161,0.4)", background: "rgba(0,180,161,0.06)", borderRadius: 18, padding: "clamp(16px,2vw,20px)", display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <span style={{ flex: "0 0 auto", width: 22, height: 22, borderRadius: 999, background: "#00B4A1", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
                      <Check style={{ width: 13, height: 13, color: "#fff" }} />
                    </span>
                    <div>
                      <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 15 }}>URL ฟรี (แนะนำ)</div>
                      <div style={{ fontSize: 13, color: "#5B7D80", marginTop: 2 }}>ใช้งานได้ทันทีโดยไม่มีค่าใช้จ่าย</div>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12.5, color: "#5B7D80", marginBottom: 6 }}>URL ของคุณ:</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <div style={{ flex: 1, background: "#fff", border: "1px solid rgba(14,58,64,0.14)", borderRadius: 12, padding: "11px 14px", fontSize: 13.5, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        saledi.web.app/s/{user?.uid?.substring(0, 8)}...
                      </div>
                      <button type="button" onClick={copyFreeUrl} disabled={isExpired} style={{ display: "flex", alignItems: "center", gap: 6, background: "#3A93B9", color: "#fff", border: 0, borderRadius: 12, padding: "11px 16px", fontSize: 13.5, fontWeight: 500, cursor: isExpired ? "not-allowed" : "pointer", whiteSpace: "nowrap", opacity: isExpired ? 0.5 : 1 }}>
                        {copied ? <Check style={{ width: 14, height: 14 }} /> : <Copy style={{ width: 14, height: 14 }} />} {copied ? "คัดลอกแล้ว" : "คัดลอก"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* URL add-on */}
                {hasUrlAddon ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div style={{ border: "1.5px solid rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.08)", borderRadius: 18, padding: "clamp(16px,2vw,20px)", display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ width: 40, height: 40, borderRadius: 999, background: "#22C55E", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <CheckCircle style={{ width: 22, height: 22, color: "#fff" }} />
                      </span>
                      <div>
                        <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 15 }}>URL Add-on เปิดใช้งานแล้ว</div>
                        <div style={{ fontSize: 13, color: "#15803D" }}>ประเภท: {urlAddonType === "path" ? "Path URL" : urlAddonType === "subdomain" ? "Subdomain" : "Both"}</div>
                      </div>
                    </div>
                    <label style={labelWrap}>
                      <span style={{ fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}><Globe style={{ width: 15, height: 15, color: "#0E7C86" }} /> Username สำหรับ Salepage</span>
                      <div style={{ position: "relative" }}>
                        <input type="text" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} placeholder="my-salepage" disabled={isExpired}
                          style={{ ...inputStyle, borderColor: usernameAvailable === true ? "#22C55E" : usernameAvailable === false ? "#EF4444" : "rgba(14,58,64,0.14)" }} />
                        <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
                          {isCheckingUsername && <Loader style={{ width: 18, height: 18, color: "#9AB0B2" }} className="animate-spin" />}
                          {!isCheckingUsername && usernameAvailable === true && <CheckCircle style={{ width: 18, height: 18, color: "#22C55E" }} />}
                          {!isCheckingUsername && usernameAvailable === false && <X style={{ width: 18, height: 18, color: "#EF4444" }} />}
                        </span>
                      </div>
                      {usernameErrorMessage && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#EF4444" }}><AlertCircle style={{ width: 14, height: 14 }} /> {usernameErrorMessage}</div>
                      )}
                      {usernameAvailable === true && username && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#16A34A" }}><CheckCircle style={{ width: 14, height: 14 }} /> ชื่อนี้ใช้งานได้</div>
                      )}
                      {username && (urlAddonType === "path" || urlAddonType === "both") && (
                        <div style={{ fontSize: 13, color: "#5B7D80" }}>🔗 Path URL: <span style={{ fontWeight: 600, color: "#E85D2A" }}>saledi.web.app/{username}</span></div>
                      )}
                    </label>
                  </div>
                ) : (
                  <div style={{ border: "1.5px solid rgba(255,122,69,0.35)", background: "rgba(255,122,69,0.06)", borderRadius: 18, padding: "clamp(16px,2vw,20px)", display: "flex", flexDirection: "column", gap: 14 }}>
                    <div>
                      <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 15 }}>URL เพิ่มเติม (Add-ons)</div>
                      <div style={{ fontSize: 13, color: "#5B7D80", marginTop: 2 }}>URL สั้นและจดจำง่าย เหมาะสำหรับแบรนด์ เหมาะสำหรับการแชร์</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "rgba(58,147,185,0.1)", borderRadius: 12, padding: "12px 14px", fontSize: 13, color: "#276E8A" }}>
                      <Info style={{ width: 15, height: 15, flex: "0 0 auto", marginTop: 1 }} /> ต้องการ URL สวยงามและจดจำง่าย? ซื้อ URL Add-on ตอนนี้!
                    </div>
                    <button type="button" onClick={() => {
                      const isFreeUser = !subscription || subscription.plan === "free";
                      if (isFreeUser) { navigate("/salepage/renew"); } else { navigate("/salepage/url-addon"); }
                    }} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "linear-gradient(120deg, #FF7A45, #E85D2A)", color: "#fff", border: 0, borderRadius: 999, padding: 14, fontFamily: HEAD, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                      <Globe style={{ width: 16, height: 16 }} /> {(!subscription || subscription.plan === "free") ? "อัพเกรดเพื่อใช้ URL Add-on" : "ซื้อ URL Add-on"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* messages */}
            {message && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 14, padding: "14px 16px", color: "#15803D", fontWeight: 600 }}>
                <CheckCircle style={{ width: 18, height: 18 }} /> {message}
              </div>
            )}
            {error && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 14, padding: "14px 16px", color: "#B91C1C", fontWeight: 600 }}>
                <AlertCircle style={{ width: 18, height: 18 }} /> {error}
              </div>
            )}
          </main>

          {/* Sticky save bar */}
          <div style={{ position: "sticky", bottom: 0, background: "rgba(255,255,255,0.94)", backdropFilter: "blur(14px)", borderTop: "1px solid rgba(14,58,64,0.08)", boxShadow: "0 -12px 30px -20px rgba(14,58,64,0.25)" }}>
            <div style={{ maxWidth: 900, margin: "0 auto", padding: "16px clamp(16px,3vw,32px)", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
              <span style={{ fontSize: 13, color: "#5B7D80" }}>การเปลี่ยนแปลงจะมีผลทันทีหลังบันทึก</span>
              <button type="button" onClick={handleSave} disabled={saving || isExpired} style={{ display: "flex", alignItems: "center", gap: 9, background: "linear-gradient(120deg, #00B4A1, #3A93B9)", color: "#fff", border: 0, borderRadius: 999, padding: "14px 26px", fontFamily: HEAD, fontSize: 15.5, fontWeight: 600, cursor: saving || isExpired ? "not-allowed" : "pointer", boxShadow: "0 16px 32px -16px rgba(0,180,161,0.5)", opacity: saving || isExpired ? 0.6 : 1 }}>
                {saving ? <><Loader style={{ width: 16, height: 16 }} className="animate-spin" /> กำลังบันทึก...</> : <><Save style={{ width: 16, height: 16 }} /> บันทึกการตั้งค่า</>}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
